import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { useSession } from '../../stores/session';
import { banners, type BannerPreset } from '../profile/banner';
import { rooms } from '../rooms/api';
import { useRoom } from '../rooms/useRoom';
import { studio } from './api';
import type { Scene } from './compositor';
import { CANVAS_HEIGHT, CANVAS_WIDTH, DEFAULT_LAYOUT, LAYOUTS, layoutById } from './layouts';
import { useStudio } from './useStudio';

/**
 * Go live from the browser, with a camera and the deck mixed together.
 *
 * ## What this replaces
 *
 * OBS, for the hosts who would otherwise need it: a scene collection, a browser
 * source pointed at an overlay URL, an audio routing setup, and a stream key
 * pasted into settings. Every one of those is a place a first broadcast dies.
 * Here the layout is a choice between four, the overlay is not a separate thing
 * to configure because the platform already knows what is playing, and the key
 * is never seen because the page fetches it.
 *
 * ## The room is still in charge of the music
 *
 * The studio does not play anything. It taps what the console is already
 * playing and mixes it with the microphone, which is what keeps play counts,
 * history and artist credit intact during a video broadcast. A host who mutes
 * the deck here mutes it in the mix, not in the room.
 *
 * ## Order matters, and the page enforces it
 *
 * The ingest server refuses a publisher whose room is not on air in stream
 * mode, so "go on air" has to happen before "start broadcasting". Rather than
 * explain that, the start button is disabled until the room is live in stream
 * mode and the go-live control here sets that mode itself.
 */
export function StudioPage() {
  const user = useSession((s) => s.user);
  const view = useRoom(undefined, { mine: true });
  const room = view.data?.room ?? null;

  const [layoutId, setLayoutId] = useState(DEFAULT_LAYOUT.id);
  const [showCard, setShowCard] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [background, setBackground] = useState<BannerPreset | null>(null);
  const [deckLevel, setDeck] = useState(0.8);
  const [micLevel, setMic] = useState(1);

  const presets = useQuery({ queryKey: ['banner-presets'], queryFn: banners.presets });

  const credentials = useQuery({
    queryKey: ['studio-credentials'],
    queryFn: studio.credentials,
    // The key is a credential. Nothing about it should sit in a cache waiting
    // for a shoulder to look over.
    gcTime: 0,
    staleTime: 0,
  });

  const scene: Scene = useMemo(
    () => ({
      layout: layoutById(layoutId),
      background: background?.stops ?? ['#0d0d10', '#1a1a20'],
      backgroundAngle: background?.angle ?? 160,
      camera: null,
      showCamera: true,
      track: room?.now_playing
        ? {
            title: room.now_playing.title,
            artist: room.now_playing.artist.display_name,
            artwork: room.now_playing.artwork ?? room.now_playing.artwork_small,
          }
        : null,
      showCard,
      brand: room
        ? {
            username: room.host.username,
            displayName: room.host.display_name,
            avatar: room.host.avatar,
          }
        : null,
      showBrand,
      live: room?.status === 'live',
    }),
    [layoutId, background, room, showCard, showBrand],
  );

  const deck = useStudio(scene);

  // The mix does not exist until the broadcast starts, so the sliders are
  // applied on change *and* re-applied here - otherwise a host who set their
  // levels before going live would find them ignored.
  useEffect(() => deck.setDeckLevel(deckLevel), [deck, deckLevel]);
  useEffect(() => deck.setMicLevel(micLevel), [deck, micLevel]);

  const goLive = useMutation({
    // `'stream'` on the way in, so the room is in the mode the ingest server
    // requires before the host can press start.
    mutationFn: () =>
      room?.status === 'live' ? rooms.endBroadcast() : rooms.goLive('stream'),
    onSuccess: () => view.refetch(),
  });

  const ready = room?.status === 'live' && room.source === 'stream';

  if (!user) return null;

  return (
    <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Studio</h1>
          <p className="mt-1 text-sm text-soft">
            Camera, overlays and your deck, mixed in the browser and sent straight out.
          </p>
        </div>

        <Link to="/console" className="text-sm text-soft underline hover:text-ink">
          Back to the console
        </Link>
      </header>

      {credentials.isError && (
        <div className="mb-6">
          <Alert tone="error">
            {credentials.error instanceof ApiError && credentials.error.status === 404
              ? 'The studio is not switched on for this platform yet.'
              : 'Could not load your publishing details.'}
          </Alert>
        </div>
      )}

      {deck.error && (
        <div className="mb-6">
          <Alert tone="error">{deck.error}</Alert>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          {/*
            The preview and the encoder's canvas.

            The second is 720p and never displayed - `hidden` rather than
            unmounted, because `captureStream` needs a real canvas in the
            document and an unmounted one produces a stream of nothing.
          */}
          <div className="overflow-hidden rounded-[var(--radius-panel)] border border-line bg-black">
            <canvas
              ref={deck.previewRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              className="block aspect-video w-full"
              data-studio-preview
            />
          </div>

          <canvas
            ref={deck.outputRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="hidden"
            aria-hidden
            data-studio-output
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant={deck.status === 'live' ? 'danger' : 'primary'}
              loading={deck.status === 'starting'}
              disabled={!ready || !credentials.data}
              onClick={() =>
                deck.status === 'live'
                  ? void deck.stop()
                  : void deck.start(credentials.data!.whip)
              }
              data-studio-toggle
            >
              {deck.status === 'live' ? 'Stop broadcasting' : 'Start broadcasting'}
            </Button>

            <Button
              variant="secondary"
              onClick={() => (deck.cameraOn ? deck.stopCamera() : void deck.startCamera())}
              aria-pressed={deck.cameraOn}
              data-studio-camera
            >
              {deck.cameraOn ? 'Camera on' : 'Enable camera'}
            </Button>

            <Button
              variant="secondary"
              onClick={() => (deck.micOn ? deck.stopMic() : void deck.startMic())}
              aria-pressed={deck.micOn}
              data-studio-mic
            >
              {deck.micOn ? 'Microphone on' : 'Enable microphone'}
            </Button>

            {deck.status === 'live' && <Meter level={deck.level} connection={deck.connection} />}
          </div>

          {!ready && (
            <p className="mt-3 text-sm text-soft">
              {room?.status === 'live'
                ? 'This room is on air from the catalogue. End the broadcast and start it again from here to switch it to the studio.'
                : 'Your room needs to be on air in studio mode before you can broadcast.'}
              <button
                type="button"
                onClick={() => goLive.mutate()}
                className="ml-2 underline hover:text-ink"
                data-studio-golive
              >
                {room?.status === 'live' ? 'End broadcast' : 'Go on air in studio mode'}
              </button>
            </p>
          )}

          {goLive.isError && (
            <p className="mt-2 text-sm text-hot-text">
              {goLive.error instanceof ApiError ? goLive.error.message : 'That did not work.'}
            </p>
          )}
        </div>

        <aside className="flex flex-col gap-6">
          <Panel title="Layout">
            <div className="grid grid-cols-2 gap-2">
              {LAYOUTS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setLayoutId(option.id)}
                  aria-pressed={layoutId === option.id}
                  title={option.hint}
                  className={`rounded-[var(--radius-control)] border px-3 py-2.5 text-left text-sm transition-colors ${
                    layoutId === option.id
                      ? 'border-accent bg-accent-wash text-ink'
                      : 'border-line text-soft hover:border-line-lit hover:text-ink'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-faint">{layoutById(layoutId).hint}</p>
          </Panel>

          <Panel title="Show">
            <Toggle label="Now playing" on={showCard} onChange={setShowCard} />
            <Toggle label="Your name" on={showBrand} onChange={setShowBrand} />
          </Panel>

          <Panel title="Background">
            <div className="flex flex-wrap gap-2">
              {(presets.data?.presets ?? []).map((preset) => (
                <button
                  key={preset.preset}
                  type="button"
                  onClick={() => setBackground(preset)}
                  aria-pressed={background?.preset === preset.preset}
                  aria-label={preset.label}
                  title={preset.label}
                  style={{
                    background: `linear-gradient(${preset.angle}deg, ${preset.stops.join(', ')})`,
                  }}
                  className={`h-9 w-9 rounded-full border-2 transition-colors ${
                    background?.preset === preset.preset ? 'border-ink' : 'border-transparent'
                  }`}
                />
              ))}
            </div>
          </Panel>

          <Panel title="Mix">
            <Slider label="Music" value={deckLevel} onChange={setDeck} />
            <Slider label="Microphone" value={micLevel} onChange={setMic} />
            <p className="mt-2 text-xs text-faint">
              These set the balance of what goes out. What you hear in the room does not change.
            </p>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-line bg-deep p-4">
      <h2 className="meta-caps mb-3 text-faint">{title}</h2>
      {children}
    </section>
  );
}

function Toggle({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between py-1.5 text-sm text-ink">
      {label}
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-[var(--color-accent)]"
      />
    </label>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <label className="block py-1.5 text-sm text-ink">
      <span className="mb-1 flex items-center justify-between">
        {label}
        <span className="font-mono text-xs text-faint">{Math.round(value * 100)}</span>
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--color-accent)]"
      />
    </label>
  );
}

/**
 * The level meter, and the connection state beside it.
 *
 * Together rather than apart because they answer the same question - "is
 * anything actually going out?" - and either one alone gives a misleading
 * answer. A moving meter with a failed connection is a host talking to nobody.
 */
function Meter({ level, connection }: { level: number; connection: RTCPeerConnectionState }) {
  const width = Math.min(100, Math.round(level * 140));

  return (
    <div className="flex items-center gap-3" data-studio-meter>
      <div className="h-2 w-32 overflow-hidden rounded-full bg-raised">
        <div
          className={`h-full transition-[width] duration-100 ${width > 92 ? 'bg-hot' : 'bg-fresh'}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="font-mono text-[11px] text-faint">{connection}</span>
    </div>
  );
}
